<template>
    <div class="announcer" ref="announcerContainer">
        <!-- Component is invisible, only handles audio -->
    </div>
</template>

<script>
import { useAuthStore } from '@/stores/auth.js'

const _audioModules = import.meta.glob('@/assets/audio/announcer/*.mp3', { eager: true })
const announcerAudio = Object.fromEntries(
    Object.entries(_audioModules).map(([path, mod]) => [
        path.split('/').pop().replace('.mp3', ''),
        mod.default,
    ])
)

export default {
    name: 'Announcer',
    data() {
        return {
            currentAudio: null,
        }
    },
    mounted() {
        // Listen for announcer events on the global service bus
        this.$serviceBus.on('announcer:speak', this.handleAnnounce)
        this.$serviceBus.on('announcer:event', this.handleEvent)

        this.$serviceBus.on('announcer:matchStart', this.handleMatchStart)
        this.$serviceBus.on('announcer:setStart', this.handleSetStart)
        this.$serviceBus.on('announcer:nextLeg', this.handleNextLeg)
        this.$serviceBus.on('announcer:finalLeg', this.handleFinalLeg)
        this.$serviceBus.on('announcer:legEnd', this.handleLegEnd)
        this.$serviceBus.on('announcer:turnEnd', this.handleTurnEnd)
        this.$serviceBus.on('announcer:matchEnd', this.handleMatchEnd)
    },
    beforeUnmount() {
        this.$serviceBus.off('announcer:speak', this.handleAnnounce)
        this.$serviceBus.off('announcer:event', this.handleEvent)

        this.$serviceBus.off('announcer:matchStart', this.handleMatchStart)
        this.$serviceBus.off('announcer:setStart', this.handleSetStart)
        this.$serviceBus.off('announcer:nextLeg', this.handleNextLeg)
        this.$serviceBus.off('announcer:finalLeg', this.handleFinalLeg)
        this.$serviceBus.off('announcer:legEnd', this.handleLegEnd)
        this.$serviceBus.off('announcer:turnEnd', this.handleTurnEnd)
        this.$serviceBus.off('announcer:matchEnd', this.handleMatchEnd)

        if (this.currentAudio) {
            this.currentAudio.pause()
            this.currentAudio = null
        }
    },
    methods: {
        /**
         * Announce a game event (gameOn, gameSetMatch, nextLeg, finalLeg, etc.)
         */
        async announceEvent(eventName) {
            if (!eventName) {
                console.warn('Announcer: Invalid event, missing eventName')
                return
            }

            try {
                await this.playAudio(eventName)
            } catch (error) {
                console.warn(`Announcer: Failed to announce event: ${eventName}`, error)
            }
        },

        /**
         * Handle match start event
         */
        async handleMatchStart() {
            console.log('Announcer: Match started')
            await this.announceEvent('gameOn')
        },

        /**
         * Handle set start event
         */
        async handleSetStart(data) {
            const { setNumber } = data
            console.log(`Announcer: Set ${setNumber} started`)
            // Only announce gameOn for new sets (not the first one, as that's matchStart)
            if (setNumber > 1) {
                await this.announceEvent('gameOn')
            }
        },

        /**
         * Handle next leg event
         */
        async handleNextLeg() {
            console.log(`Announcer: Next leg started`)
            try {
                if (this.currentAudio) {
                    this.currentAudio.pause()
                    this.currentAudio = null
                }
                await this.announceEvent('nextLeg')
            } catch (error) {
                console.error('Announcer: Error announcing next leg', error)
            }
        },

        /**
         * Handle final leg event
         */
        async handleFinalLeg() {
            console.log(`Announcer: Final leg started`)
            try {
                if (this.currentAudio) {
                    this.currentAudio.pause()
                    this.currentAudio = null
                }
                await this.announceEvent('finalLeg')
            } catch (error) {
                console.error('Announcer: Error announcing final leg', error)
            }
        },

        /**
         * Handle leg end event
         */
        async handleLegEnd(data) {
            const { winnerId, legNumber } = data
            console.log(`Announcer: Leg ${legNumber} ended, winner: ${winnerId}`)
            // Log for now, can be expanded later if needed
        },

        /**
         * Handle turn end event (score announcement)
         */
        async handleTurnEnd(data) {
            const { score, teamId } = data
            console.log(`Announcer: Turn ended with score ${score} for team ${teamId}`)

            try {
                if (this.currentAudio) {
                    this.currentAudio.pause()
                    this.currentAudio = null
                }

                await this.playAudio(String(score))
            } catch (error) {
                console.warn(`Announcer: Failed to announce score: ${score}`, error)
            }
        },

        /**
         * Handle match end event (game set match)
         */
        async handleMatchEnd(data) {
            const { winnerId } = data
            console.log(`Announcer: Match ended, winner: ${winnerId}`)

            try {
                if (this.currentAudio) {
                    this.currentAudio.pause()
                    this.currentAudio = null
                }

                await this.announceEvent('gameSetMatch')
            } catch (error) {
                console.error('Announcer: Error announcing match end', error)
            }
        },

        /**
         * Handle game event announcements (legacy, for backwards compatibility)
         */
        async handleEvent(event) {
            const { eventName } = event

            if (!eventName) {
                console.warn('Announcer: Invalid event, missing eventName')
                return
            }

            try {
                // Stop any currently playing audio
                if (this.currentAudio) {
                    this.currentAudio.pause()
                    this.currentAudio = null
                }

                console.log(`Announcer: Announcing event: ${eventName}`)
                await this.announceEvent(eventName)
            } catch (error) {
                console.error('Announcer: Error handling event announcement', error)
            }
        },

        async handleAnnounce(event) {
            const { score, requiresPrefix } = event

            if (!score) {
                console.warn('Announcer: Invalid event, missing score')
                return
            }

            try {
                // Stop any currently playing audio
                if (this.currentAudio) {
                    this.currentAudio.pause()
                    this.currentAudio = null
                }

                // Build audio file paths
                const audioFiles = []

                if (requiresPrefix) {
                    audioFiles.push('you_require')
                }

                audioFiles.push(String(score))

                // Play audio files sequentially
                await this.playAudioSequence(audioFiles)
            } catch (error) {
                console.error('Announcer: Error handling announcement', error)
            }
        },

        async playAudioSequence(audioFiles) {
            for (const audioFile of audioFiles) {
                // Continue to next file even if current one fails
                try {
                    await this.playAudio(audioFile)
                } catch (error) {
                    console.warn(`Announcer: Skipping file ${audioFile}, continuing to next`, error)
                    continue
                }
            }
        },

        playAudio(key) {
            const authStore = useAuthStore()
            if (authStore.profile?.announcer_enabled === false) return Promise.resolve()

            const url = announcerAudio[key]
            if (!url) {
                console.warn(`Announcer: No audio file for key: ${key}`)
                return Promise.resolve()
            }

            return new Promise((resolve, reject) => {
                try {
                    this.currentAudio = new Audio(url)

                    this.currentAudio.onended = () => {
                        this.currentAudio = null
                        resolve()
                    }

                    this.currentAudio.onerror = () => {
                        console.error(`Announcer: Failed to load audio: ${key}`)
                        this.currentAudio = null
                        reject(new Error(`Failed to load audio: ${key}`))
                    }

                    this.currentAudio.play().catch((error) => {
                        console.error(`Announcer: Failed to play audio: ${key}`, error)
                        reject(error)
                    })
                } catch (error) {
                    console.error(`Announcer: Error creating audio element for: ${key}`, error)
                    reject(error)
                }
            })
        },
    },
}
</script>

<style scoped>
.announcer {
    display: none;
}
</style>
